/**
 * TimerStopForm - Side panel to save recorded time
 *
 * Shows:
 * - Start date/time
 * - End date/time
 * - Duration
 * - Description field
 * - Billable toggle
 *
 * Pattern: DetailPanel (right side panel, mobile = full screen)
 */

import { useState } from 'react';
import { Clock, FileText, Receipt, Trash2, Check } from 'lucide-react';
import type { PendingTimerSummary } from '@business-assistant/shared';
import { formatDuration } from '@business-assistant/shared';
import { DetailPanel, DetailSection, DetailField, DetailActions } from '../common/base';
import { useTranslation } from '../../hooks';
import { TextAreaField, Button } from '../common/forms';

interface TimerStopFormProps {
  isOpen: boolean;
  summary: PendingTimerSummary;
  taskTitle?: string;
  projectTitle?: string;
  onConfirm: (data: { description: string; billable: boolean }) => void;
  onDiscard: () => void;
}

// =============================================================================
// STYLES
// =============================================================================

const styles = {
  durationDisplay: {
    textAlign: 'center',
    padding: '20px',
    background: 'var(--bg, #f5f5f5)',
    borderRadius: '12px',
    marginBottom: '16px',
  } as React.CSSProperties,
  durationValue: {
    fontSize: '32px',
    fontWeight: 700,
    color: 'var(--brand, #f59e0b)',
    display: 'block',
  } as React.CSSProperties,
  durationLabel: {
    fontSize: '13px',
    color: 'var(--text-muted, #666)',
    marginTop: '4px',
    display: 'block',
  } as React.CSSProperties,
  timeRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: '1px solid var(--line, #eee)',
  } as React.CSSProperties,
  timeLabel: {
    fontSize: '13px',
    color: 'var(--text-muted, #666)',
  } as React.CSSProperties,
  timeValue: {
    fontSize: '14px',
    fontWeight: 500,
  } as React.CSSProperties,
  projectBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    background: 'var(--bg, #f5f5f5)',
    borderRadius: '6px',
    fontSize: '13px',
  } as React.CSSProperties,
  toggleContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  } as React.CSSProperties,
  toggleLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
  } as React.CSSProperties,
  toggleHint: {
    fontSize: '12px',
    color: 'var(--text-muted, #666)',
    marginLeft: '26px',
  } as React.CSSProperties,
};

// =============================================================================
// COMPONENT
// =============================================================================

export function TimerStopForm({
  isOpen,
  summary,
  taskTitle,
  projectTitle,
  onConfirm,
  onDiscard,
}: TimerStopFormProps) {
  const { t, language } = useTranslation();
  const [description, setDescription] = useState(summary.description || '');
  const [billable, setBillable] = useState(true);

  const startDate = new Date(summary.startedAt);
  const endDate = new Date(summary.stoppedAt);
  const locale = language === 'it' ? 'it-IT' : 'en-US';

  const formatDateTime = (date: Date) => {
    return date.toLocaleString(locale, {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleSubmit = () => {
    onConfirm({ description, billable });
  };

  return (
    <DetailPanel
      isOpen={isOpen}
      onClose={onDiscard}
      title={t('timer.recordedTime')}
      subtitle={taskTitle || t('timer.saveTime')}
      icon={<Clock size={18} />}
      headerColor="#f59e0b"
      width="50%"
      footer={
        <DetailActions>
          <Button variant="danger" icon={Trash2} onClick={onDiscard}>
            {t('timer.discard')}
          </Button>
          <Button variant="primary" icon={Check} onClick={handleSubmit}>
            {t('timer.saveTime')}
          </Button>
        </DetailActions>
      }
    >
      {/* Duration Display */}
      <div style={styles.durationDisplay}>
        <span style={styles.durationValue}>
          {formatDuration(summary.durationMinutes)}
        </span>
        <span style={styles.durationLabel}>
          {t('timer.minutes', { count: summary.durationMinutes })}
        </span>
      </div>

      {/* Time Range */}
      <DetailSection title={t('timer.period')}>
        <div style={styles.timeRow}>
          <span style={styles.timeLabel}>{t('timer.start')}</span>
          <span style={styles.timeValue}>{formatDateTime(startDate)}</span>
        </div>
        <div style={{ ...styles.timeRow, borderBottom: 'none' }}>
          <span style={styles.timeLabel}>{t('timer.end')}</span>
          <span style={styles.timeValue}>{formatDateTime(endDate)}</span>
        </div>
      </DetailSection>

      {/* Project */}
      {projectTitle && (
        <DetailSection title={t('nav.projects')}>
          <div style={styles.projectBadge}>
            <FileText size={14} />
            <span>{projectTitle}</span>
          </div>
        </DetailSection>
      )}

      {/* Description */}
      <DetailSection title={t('timer.description')}>
        <TextAreaField
          name="description"
          label=""
          value={description}
          onChange={setDescription}
          placeholder={t('timer.descriptionPlaceholder')}
          rows={3}
        />
      </DetailSection>

      {/* Billable Toggle */}
      <DetailSection title={t('timer.billing')}>
        <div style={styles.toggleContainer}>
          <label style={styles.toggleLabel}>
            <input
              type="checkbox"
              checked={billable}
              onChange={(e) => setBillable(e.target.checked)}
              style={{ width: '16px', height: '16px' }}
            />
            <Receipt size={16} />
            <span>{t('timer.billable')}</span>
          </label>
          {billable && (
            <span style={styles.toggleHint}>
              {t('timer.billableHint')}
            </span>
          )}
        </div>
      </DetailSection>
    </DetailPanel>
  );
}

export default TimerStopForm;
